// src/index.ts
import { Qr20Error, canonicalizeQr20Json } from "@qr20/envelope";
import {
  eip191Digest,
  eip712Digest as cryptoEip712Digest,
  hashStruct as cryptoHashStruct,
  recoverEthereumAddress as cryptoRecoverEthereumAddress,
  verifyEcdsaSecp256k1
} from "@qr20/crypto";
var QR20_SIG_SCHEMES = [
  "secp256k1-eip191",
  "secp256k1-eip712",
  "secp256k1-sha256",
  "ed25519"
];
var QR20_CHANNEL_DISPUTE_WINDOW_BOUNDS = {
  minSeconds: 3600,
  maxSeconds: 2592e3
};
var QR20_CHANNEL_RECLAIM_WINDOW_FLOOR_SECONDS = 7 * 24 * 3600;
var QR20_CHANNEL_AMOUNT_PATTERN = /^(0|[1-9][0-9]*)$/;
var QR20_CHANNEL_INTENT_DIGEST_PATTERN = /^0x[0-9a-f]{64}$/;
var QR20_CHANNEL_CAIP2_PATTERN = /^[a-z0-9]+:[a-zA-Z0-9-_]+$/;
var QR20_CHANNEL_ASSET_REF_PATTERN = /^[a-z0-9]+:[a-zA-Z0-9-_]+\/[a-z0-9]+:[a-zA-Z0-9:._/-]+$/;
var QR20_CHANNEL_CAIP10_PATTERN = /^[a-z0-9]+:[a-zA-Z0-9-_]+\/[a-zA-Z0-9:._-]+$/;
var QR20_CHANNEL_RAW_EVM_ADDRESS_PATTERN = /^0x[0-9a-fA-F]{40}$/;
var QR20_CHANNEL_ISO8601_UTC_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;
var SIGNATURE_VALUE_PATTERN = /^0x[0-9a-f]+$/;
var QR20_CHANNEL_EVENT_NAMES = [
  "ChannelOpened",
  "ChannelCloseInitiated",
  "ChannelChallenged",
  "ChannelClosed",
  "FundsReclaimed"
];
var QR20_CHANNEL_STATES = [
  "Open",
  "Disputing",
  "Closed",
  "Reclaimed"
];
var QR20_CHANNEL_TRANSITION_TRIGGERS = [
  "openChannel",
  "topUp",
  "stateUpdate",
  "closeChannel",
  "finalize",
  "reclaimFunds"
];
var Qr20ChannelNotImplementedError = class extends Qr20Error {
  code = "QR20_CHANNEL_NOT_IMPLEMENTED";
  constructor(message, options = {}) {
    super(message, options);
    this.name = "Qr20ChannelNotImplementedError";
  }
};
var Qr20ChannelSignatureRejectedError = class extends Qr20Error {
  code = "QR20_CHANNEL_SIGNATURE_REJECTED";
  constructor(message, options = {}) {
    super(message, options);
    this.name = "Qr20ChannelSignatureRejectedError";
  }
};
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function pushError(errors, path, message) {
  errors.push({ path, message });
}
function validateSignature(value, path, errors, openSigSchemes) {
  if (!isRecord(value)) {
    pushError(errors, path, "Expected a signature object.");
    return;
  }
  const knownKeys = ["scheme", "value"];
  Object.keys(value).forEach((key) => {
    if (!knownKeys.includes(key)) {
      pushError(errors, `${path}.${key}`, "Unexpected field.");
    }
  });
  if (typeof value.scheme !== "string" || !QR20_SIG_SCHEMES.includes(value.scheme)) {
    pushError(errors, `${path}.scheme`, `Expected one of: ${QR20_SIG_SCHEMES.join(", ")}.`);
  } else if (openSigSchemes && !openSigSchemes.includes(value.scheme)) {
    pushError(
      errors,
      `${path}.scheme`,
      `Scheme ${value.scheme} is not in the channel's opened sig_schemes set.`
    );
  }
  if (typeof value.value !== "string" || !SIGNATURE_VALUE_PATTERN.test(value.value)) {
    pushError(errors, `${path}.value`, "Expected a 0x-prefixed lowercase hex string.");
  }
}
function validateBalances(value, path, errors) {
  if (!isRecord(value)) {
    pushError(errors, path, "Expected a balances object.");
    return void 0;
  }
  const knownKeys = ["creator_amount", "resolver_amount"];
  Object.keys(value).forEach((key) => {
    if (!knownKeys.includes(key)) {
      pushError(errors, `${path}.${key}`, "Unexpected field.");
    }
  });
  let ok = true;
  for (const key of knownKeys) {
    const entry = value[key];
    if (typeof entry !== "string" || !QR20_CHANNEL_AMOUNT_PATTERN.test(entry)) {
      pushError(errors, `${path}.${key}`, "Expected a canonical decimal-string amount.");
      ok = false;
    }
  }
  if (!ok) {
    return void 0;
  }
  return {
    creator_amount: value.creator_amount,
    resolver_amount: value.resolver_amount
  };
}
var PHASE_REQUIRED_FIELDS = {
  open: ["contract_ref", "asset_ref", "sig_schemes", "deposit", "dispute_window_s"],
  topUp: ["contract_ref", "deposit"],
  update: ["balances", "intent_digest", "creator_sig", "resolver_sig"],
  close: ["balances", "intent_digest", "creator_sig", "resolver_sig"],
  reclaim: ["contract_ref"],
  plain: []
};
function inferChannelPhase(channel) {
  const hasUpdate = channel.balances !== void 0 && channel.intent_digest !== void 0 && channel.creator_sig !== void 0 && channel.resolver_sig !== void 0;
  if (hasUpdate) {
    return "update";
  }
  if (channel.deposit !== void 0) {
    if (channel.sig_schemes !== void 0 && channel.dispute_window_s !== void 0) {
      return "open";
    }
    return "topUp";
  }
  if (channel.contract_ref !== void 0 && channel.balances === void 0 && channel.deposit === void 0) {
    return "reclaim";
  }
  return "plain";
}
function validateExtendedChannel(value, context = {}) {
  const errors = [];
  if (!isRecord(value)) {
    pushError(errors, "$", "Expected a channel object.");
    return { ok: false, errors };
  }
  const channel = value;
  const allowedKeys = [
    "channel_id",
    "sequence",
    "contract_ref",
    "asset_ref",
    "sig_schemes",
    "deposit",
    "balances",
    "intent_digest",
    "creator_sig",
    "resolver_sig",
    "dispute_window_s"
  ];
  Object.keys(channel).forEach((key) => {
    if (!allowedKeys.includes(key)) {
      pushError(errors, `$.${key}`, "Unexpected field.");
    }
  });
  if (typeof channel.channel_id !== "string" || channel.channel_id.trim().length === 0) {
    pushError(errors, "$.channel_id", "Expected a non-empty string.");
  }
  if (!Number.isInteger(channel.sequence) || channel.sequence < 0) {
    pushError(errors, "$.sequence", "Expected an integer >= 0.");
  }
  if (channel.contract_ref !== void 0) {
    if (typeof channel.contract_ref !== "string" || !QR20_CHANNEL_CAIP10_PATTERN.test(channel.contract_ref)) {
      pushError(
        errors,
        "$.contract_ref",
        "Expected a CAIP-10 contract reference string (QIP-002 \xA74.3.1)."
      );
    }
  }
  if (channel.asset_ref !== void 0) {
    if (typeof channel.asset_ref !== "string" || !QR20_CHANNEL_ASSET_REF_PATTERN.test(channel.asset_ref)) {
      pushError(errors, "$.asset_ref", "Expected a CAIP-19 asset-reference string.");
    }
  }
  if (channel.sig_schemes !== void 0) {
    if (!Array.isArray(channel.sig_schemes) || channel.sig_schemes.length === 0) {
      pushError(errors, "$.sig_schemes", "Expected a non-empty array.");
    } else {
      const seen = /* @__PURE__ */ new Set();
      channel.sig_schemes.forEach((entry, index) => {
        if (typeof entry !== "string" || !QR20_SIG_SCHEMES.includes(entry)) {
          pushError(
            errors,
            `$.sig_schemes[${index}]`,
            `Expected one of: ${QR20_SIG_SCHEMES.join(", ")}.`
          );
        } else if (seen.has(entry)) {
          pushError(errors, `$.sig_schemes[${index}]`, "Duplicate scheme.");
        } else {
          seen.add(entry);
        }
      });
    }
  }
  if (channel.deposit !== void 0) {
    if (typeof channel.deposit !== "string" || !QR20_CHANNEL_AMOUNT_PATTERN.test(channel.deposit)) {
      pushError(errors, "$.deposit", "Expected a canonical decimal-string amount.");
    }
  }
  let balances;
  if (channel.balances !== void 0) {
    balances = validateBalances(channel.balances, "$.balances", errors);
  }
  if (channel.intent_digest !== void 0) {
    if (typeof channel.intent_digest !== "string" || !QR20_CHANNEL_INTENT_DIGEST_PATTERN.test(channel.intent_digest)) {
      pushError(errors, "$.intent_digest", "Expected 0x + 64 lowercase hex (66 chars).");
    }
  }
  if (channel.creator_sig !== void 0) {
    validateSignature(channel.creator_sig, "$.creator_sig", errors, context.openSigSchemes);
  }
  if (channel.resolver_sig !== void 0) {
    validateSignature(channel.resolver_sig, "$.resolver_sig", errors, context.openSigSchemes);
  }
  if (channel.dispute_window_s !== void 0) {
    const secs = channel.dispute_window_s;
    if (!Number.isInteger(secs) || secs < QR20_CHANNEL_DISPUTE_WINDOW_BOUNDS.minSeconds || secs > QR20_CHANNEL_DISPUTE_WINDOW_BOUNDS.maxSeconds) {
      pushError(
        errors,
        "$.dispute_window_s",
        `Expected an integer in [${QR20_CHANNEL_DISPUTE_WINDOW_BOUNDS.minSeconds}, ${QR20_CHANNEL_DISPUTE_WINDOW_BOUNDS.maxSeconds}].`
      );
    }
  }
  const phase = context.phase ?? inferChannelPhase(channel);
  const requiredFieldsForPhase = PHASE_REQUIRED_FIELDS[phase];
  for (const field of requiredFieldsForPhase) {
    if (channel[field] === void 0) {
      pushError(
        errors,
        `$.${field}`,
        `Required for ${phase} phase (QIP-002 \xA74.3.2).`
      );
    }
  }
  if ((phase === "update" || phase === "close") && balances !== void 0 && context.totalDeposited !== void 0) {
    try {
      const creator = BigInt(balances.creator_amount);
      const resolver = BigInt(balances.resolver_amount);
      const total = BigInt(context.totalDeposited);
      if (creator + resolver !== total) {
        pushError(
          errors,
          "$.balances",
          `Expected creator_amount + resolver_amount == ${context.totalDeposited} (balance conservation, QIP-002 \xA74.1.3 I-3).`
        );
      }
    } catch {
      pushError(errors, "$.balances", "Non-integer amount string(s) prevented conservation check.");
    }
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, value: channel, errors };
}
function validateReclaimWindow(disputeWindowSeconds, reclaimWindowSeconds) {
  const errors = [];
  const { minSeconds, maxSeconds } = QR20_CHANNEL_DISPUTE_WINDOW_BOUNDS;
  const floor = QR20_CHANNEL_RECLAIM_WINDOW_FLOOR_SECONDS;
  const disputeOk = Number.isInteger(disputeWindowSeconds) && disputeWindowSeconds >= minSeconds && disputeWindowSeconds <= maxSeconds;
  if (!disputeOk) {
    pushError(
      errors,
      "$.disputeWindowSeconds",
      `Expected an integer in [${minSeconds}, ${maxSeconds}] (QIP-002 \xA74.3.1 / \xA74.5).`
    );
  }
  const reclaimOk = Number.isInteger(reclaimWindowSeconds) && reclaimWindowSeconds >= floor;
  if (!reclaimOk) {
    pushError(
      errors,
      "$.reclaimWindowSeconds",
      `Expected an integer >= ${floor} (QIP-002 \xA74.5.2 reclaim-window floor of 7 days).`
    );
  }
  if (disputeOk && reclaimOk && reclaimWindowSeconds <= disputeWindowSeconds) {
    pushError(
      errors,
      "$.reclaimWindowSeconds",
      `Expected reclaimWindowSeconds (${reclaimWindowSeconds}) > disputeWindowSeconds (${disputeWindowSeconds}) (QIP-002 \xA74.5.2: the reclaim path must never shortcut an active dispute).`
    );
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return {
    ok: true,
    value: { disputeWindowSeconds, reclaimWindowSeconds },
    errors
  };
}
var CHANNEL_TRANSITION_TABLE = /* @__PURE__ */ new Map([
  ["null|openChannel", "Open"],
  ["Open|topUp", "Open"],
  ["Open|stateUpdate", "Open"],
  ["Open|closeChannel", "Disputing"],
  ["Open|reclaimFunds", "Reclaimed"],
  ["Disputing|closeChannel", "Disputing"],
  ["Disputing|finalize", "Closed"]
]);
function transitionKey(from, trigger) {
  return `${from ?? "null"}|${trigger}`;
}
function validateStateTransition(from, trigger) {
  const errors = [];
  const fromOk = from === null || QR20_CHANNEL_STATES.includes(from);
  if (!fromOk) {
    pushError(
      errors,
      "$.from",
      `Expected null or one of: ${QR20_CHANNEL_STATES.join(", ")}.`
    );
  }
  const triggerOk = QR20_CHANNEL_TRANSITION_TRIGGERS.includes(trigger);
  if (!triggerOk) {
    pushError(
      errors,
      "$.trigger",
      `Expected one of: ${QR20_CHANNEL_TRANSITION_TRIGGERS.join(", ")}.`
    );
  }
  if (!fromOk || !triggerOk) {
    return { ok: false, errors };
  }
  const to = CHANNEL_TRANSITION_TABLE.get(transitionKey(from, trigger));
  if (!to) {
    pushError(
      errors,
      "$",
      `Trigger "${trigger}" is not permitted from state "${from ?? "null"}" (QIP-002 \xA74.1.2).`
    );
    return { ok: false, errors };
  }
  return { ok: true, value: { from, trigger, to }, errors };
}
function classifyStateUpdateReplay(incoming, stored) {
  const errors = [];
  if (!isNonNegativeInteger(incoming.sequence)) {
    pushError(
      errors,
      "$.incoming.sequence",
      "Expected a non-negative integer (QIP-002 \xA74.3.1 sequence schema)."
    );
  }
  if (stored !== null && !isNonNegativeInteger(stored.sequence)) {
    pushError(
      errors,
      "$.stored.sequence",
      "Expected a non-negative integer (QIP-002 \xA74.3.1 sequence schema)."
    );
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  if (stored === null) {
    return { ok: true, value: { classification: "initial" }, errors };
  }
  if (incoming.channel_id !== stored.channel_id) {
    pushError(
      errors,
      "$.incoming.channel_id",
      `Expected channel_id "${stored.channel_id}" to match the stored state; got "${incoming.channel_id}".`
    );
    return { ok: false, errors };
  }
  if (incoming.sequence > stored.sequence) {
    return { ok: true, value: { classification: "progressing" }, errors };
  }
  if (incoming.sequence < stored.sequence) {
    return { ok: true, value: { classification: "stale" }, errors };
  }
  const balancesMatch = incoming.balances.creator_amount === stored.balances.creator_amount && incoming.balances.resolver_amount === stored.balances.resolver_amount;
  const digestMatch = incoming.intent_digest === stored.intent_digest;
  if (balancesMatch && digestMatch) {
    return { ok: true, value: { classification: "duplicate" }, errors };
  }
  if (!balancesMatch) {
    pushError(
      errors,
      "$.incoming.balances",
      `Re-delivery at sequence ${incoming.sequence} carries different balances than the stored state (QIP-002 \xA74.1.4 rule 1: protocol violation).`
    );
  }
  if (!digestMatch) {
    pushError(
      errors,
      "$.incoming.intent_digest",
      `Re-delivery at sequence ${incoming.sequence} carries a different intent_digest than the stored state (QIP-002 \xA74.1.4 rule 1: protocol violation).`
    );
  }
  return { ok: false, errors };
}
function compareDualSignedStates(incoming, stored) {
  const errors = [];
  if (!isNonNegativeInteger(incoming.sequence)) {
    pushError(
      errors,
      "$.incoming.sequence",
      "Expected a non-negative integer (QIP-002 \xA74.3.1 sequence schema)."
    );
  }
  if (!isNonNegativeInteger(stored.sequence)) {
    pushError(
      errors,
      "$.stored.sequence",
      "Expected a non-negative integer (QIP-002 \xA74.3.1 sequence schema)."
    );
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  if (incoming.channel_id !== stored.channel_id) {
    pushError(
      errors,
      "$.incoming.channel_id",
      `Expected channel_id "${stored.channel_id}" to match the stored dispute state; got "${incoming.channel_id}".`
    );
    return { ok: false, errors };
  }
  if (incoming.sequence === stored.sequence) {
    pushError(
      errors,
      "$.incoming.sequence",
      `Challenge rejected: incoming sequence ${incoming.sequence} equals stored ${stored.sequence}; QIP-002 \xA74.5.1 rule 2 requires a strictly newer sequence (a single sequence has exactly one fully-signed state).`
    );
    return { ok: false, errors };
  }
  if (incoming.sequence < stored.sequence) {
    pushError(
      errors,
      "$.incoming.sequence",
      `Challenge rejected: incoming sequence ${incoming.sequence} is older than stored ${stored.sequence}; QIP-002 \xA74.5.1 rule 2 requires a strictly newer sequence.`
    );
    return { ok: false, errors };
  }
  return {
    ok: true,
    value: { channel_id: incoming.channel_id, sequence: incoming.sequence },
    errors
  };
}
var CHANNEL_EVENT_PAYLOAD_SCHEMA = {
  ChannelOpened: {
    required: [
      "creator",
      "resolver",
      "asset_ref",
      "deposit",
      "dispute_window_s",
      "reclaim_window_s",
      "sig_schemes_digest"
    ],
    amountFields: ["deposit"],
    digestFields: ["sig_schemes_digest"],
    addressFields: ["creator", "resolver"],
    integerFields: ["dispute_window_s", "reclaim_window_s"],
    assetRefFields: ["asset_ref"]
  },
  ChannelCloseInitiated: {
    required: [
      "closer",
      "sequence",
      "creator_amount",
      "resolver_amount",
      "intent_digest",
      "expires_at"
    ],
    amountFields: ["creator_amount", "resolver_amount"],
    digestFields: ["intent_digest"],
    addressFields: ["closer"],
    integerFields: ["sequence", "expires_at"],
    assetRefFields: []
  },
  ChannelChallenged: {
    required: [
      "challenger",
      "sequence",
      "creator_amount",
      "resolver_amount",
      "intent_digest",
      "expires_at"
    ],
    amountFields: ["creator_amount", "resolver_amount"],
    digestFields: ["intent_digest"],
    addressFields: ["challenger"],
    integerFields: ["sequence", "expires_at"],
    assetRefFields: []
  },
  ChannelClosed: {
    required: ["sequence", "creator_amount", "resolver_amount", "intent_digest"],
    amountFields: ["creator_amount", "resolver_amount"],
    digestFields: ["intent_digest"],
    addressFields: [],
    integerFields: ["sequence"],
    assetRefFields: []
  },
  FundsReclaimed: {
    required: ["reclaimer", "sequence_at_reclaim", "creator_amount", "resolver_amount"],
    amountFields: ["creator_amount", "resolver_amount"],
    digestFields: [],
    addressFields: ["reclaimer"],
    integerFields: ["sequence_at_reclaim"],
    assetRefFields: []
  }
};
function isNonNegativeInteger(value) {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}
function isAddressLike(value) {
  return typeof value === "string" && (QR20_CHANNEL_CAIP10_PATTERN.test(value) || QR20_CHANNEL_RAW_EVM_ADDRESS_PATTERN.test(value));
}
function extractCaip2Prefix(caip10) {
  if (typeof caip10 !== "string") return void 0;
  const slash = caip10.indexOf("/");
  if (slash <= 0) return void 0;
  return caip10.slice(0, slash);
}
function validateNormalisedChannelEvent(value, context = {}) {
  const errors = [];
  const ctx = context ?? {};
  let conservationTotal;
  if (ctx.totalDeposited !== void 0) {
    if (typeof ctx.totalDeposited !== "string" || !QR20_CHANNEL_AMOUNT_PATTERN.test(ctx.totalDeposited)) {
      pushError(
        errors,
        "$.context.totalDeposited",
        "Expected a canonical decimal-string amount (QIP-002 \xA74.2.2)."
      );
    } else {
      conservationTotal = ctx.totalDeposited;
    }
  }
  if (!isRecord(value)) {
    pushError(errors, "$", "Expected a normalised channel-event object.");
    return { ok: false, errors };
  }
  const event = value;
  let resolvedChainId;
  if (typeof event.chain_id !== "string" || !QR20_CHANNEL_CAIP2_PATTERN.test(event.chain_id)) {
    pushError(errors, "$.chain_id", 'Expected a CAIP-2 chain identifier (e.g. "eip155:1").');
  } else {
    resolvedChainId = event.chain_id;
  }
  if (typeof event.contract_ref !== "string" || !QR20_CHANNEL_CAIP10_PATTERN.test(event.contract_ref)) {
    pushError(
      errors,
      "$.contract_ref",
      'Expected a CAIP-10 account reference (e.g. "eip155:1/address:0x\u2026").'
    );
  } else if (resolvedChainId) {
    const prefix = extractCaip2Prefix(event.contract_ref);
    if (prefix !== resolvedChainId) {
      pushError(
        errors,
        "$.contract_ref",
        `CAIP-2 prefix "${prefix}" must equal top-level chain_id "${resolvedChainId}" (QIP-002 \xA74.6.4).`
      );
    }
  }
  if (!isNonNegativeInteger(event.block_number)) {
    pushError(errors, "$.block_number", "Expected a non-negative integer.");
  }
  if (typeof event.block_time !== "string" || !QR20_CHANNEL_ISO8601_UTC_PATTERN.test(event.block_time)) {
    pushError(
      errors,
      "$.block_time",
      'Expected an ISO-8601 UTC timestamp (e.g. "2026-05-09T12:34:56Z").'
    );
  }
  if (typeof event.tx_hash !== "string" || event.tx_hash.length === 0) {
    pushError(errors, "$.tx_hash", "Expected a non-empty string.");
  }
  if (!isNonNegativeInteger(event.log_index)) {
    pushError(errors, "$.log_index", "Expected a non-negative integer.");
  }
  let resolvedEventName;
  if (typeof event.event_name !== "string" || !QR20_CHANNEL_EVENT_NAMES.includes(event.event_name)) {
    pushError(
      errors,
      "$.event_name",
      `Expected one of: ${QR20_CHANNEL_EVENT_NAMES.join(", ")}.`
    );
  } else {
    resolvedEventName = event.event_name;
  }
  if (typeof event.channel_id !== "string" || event.channel_id.length === 0) {
    pushError(errors, "$.channel_id", "Expected a non-empty string.");
  }
  if (!isRecord(event.payload)) {
    pushError(errors, "$.payload", "Expected a payload object.");
  } else if (resolvedEventName) {
    const schema = CHANNEL_EVENT_PAYLOAD_SCHEMA[resolvedEventName];
    const payload = event.payload;
    for (const key of schema.required) {
      if (!(key in payload)) {
        pushError(
          errors,
          `$.payload.${key}`,
          `Missing required field for event "${resolvedEventName}" (QIP-002 \xA74.6.1).`
        );
      }
    }
    for (const key of schema.amountFields) {
      const entry = payload[key];
      if (entry === void 0) continue;
      if (typeof entry !== "string" || !QR20_CHANNEL_AMOUNT_PATTERN.test(entry)) {
        pushError(
          errors,
          `$.payload.${key}`,
          "Expected a canonical decimal-string amount (QIP-002 \xA74.2.2 / \xA74.6.4)."
        );
      }
    }
    for (const key of schema.digestFields) {
      const entry = payload[key];
      if (entry === void 0) continue;
      if (typeof entry !== "string" || !QR20_CHANNEL_INTENT_DIGEST_PATTERN.test(entry)) {
        pushError(
          errors,
          `$.payload.${key}`,
          "Expected 0x + 64 lowercase hex (66 chars)."
        );
      }
    }
    for (const key of schema.addressFields) {
      const entry = payload[key];
      if (entry === void 0) continue;
      if (!isAddressLike(entry)) {
        pushError(
          errors,
          `$.payload.${key}`,
          "Expected a CAIP-10 account reference or raw 0x-prefixed 20-byte EVM address (QIP-002 \xA74.6.4)."
        );
        continue;
      }
      if (resolvedChainId && typeof entry === "string" && QR20_CHANNEL_CAIP10_PATTERN.test(entry)) {
        const prefix = extractCaip2Prefix(entry);
        if (prefix !== resolvedChainId) {
          pushError(
            errors,
            `$.payload.${key}`,
            `CAIP-2 prefix "${prefix}" must equal top-level chain_id "${resolvedChainId}" (QIP-002 \xA74.6.4).`
          );
        }
      }
    }
    for (const key of schema.integerFields) {
      const entry = payload[key];
      if (entry === void 0) continue;
      if (!isNonNegativeInteger(entry)) {
        pushError(errors, `$.payload.${key}`, "Expected a non-negative integer.");
      }
    }
    for (const key of schema.assetRefFields) {
      const entry = payload[key];
      if (entry === void 0) continue;
      if (typeof entry !== "string" || !QR20_CHANNEL_ASSET_REF_PATTERN.test(entry)) {
        pushError(
          errors,
          `$.payload.${key}`,
          "Expected a CAIP-19 asset-reference string."
        );
      }
    }
    if (conservationTotal !== void 0 && schema.amountFields.includes("creator_amount") && schema.amountFields.includes("resolver_amount")) {
      const creatorRaw = payload.creator_amount;
      const resolverRaw = payload.resolver_amount;
      if (typeof creatorRaw === "string" && typeof resolverRaw === "string" && QR20_CHANNEL_AMOUNT_PATTERN.test(creatorRaw) && QR20_CHANNEL_AMOUNT_PATTERN.test(resolverRaw)) {
        const creator = BigInt(creatorRaw);
        const resolver = BigInt(resolverRaw);
        const total = BigInt(conservationTotal);
        if (creator + resolver !== total) {
          pushError(
            errors,
            "$.payload",
            `Expected creator_amount + resolver_amount == ${conservationTotal} for ${resolvedEventName} (balance conservation, QIP-002 \xA74.1.3 I-3 / \xA74.6.1).`
          );
        }
      }
    }
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return {
    ok: true,
    value: event,
    errors
  };
}
var TEXT_ENCODER = /* @__PURE__ */ new TextEncoder();
function requireSubtle() {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Qr20ChannelNotImplementedError(
      "globalThis.crypto.subtle is unavailable in this runtime.",
      {
        hint: "WebCrypto is required for QIP-002 SHA-256 and Ed25519 helpers.",
        remediation: "Run on Node.js \u2265 18 (globalThis.crypto is pre-installed) or a modern browser; otherwise polyfill globalThis.crypto."
      }
    );
  }
  return subtle;
}
function utf8(value) {
  return TEXT_ENCODER.encode(value);
}
function bytesToLowerHex(bytes) {
  let hex = "0x";
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, "0");
  }
  return hex;
}
function digestHexToBytes(value) {
  if (!QR20_CHANNEL_INTENT_DIGEST_PATTERN.test(value)) {
    throw new Qr20ChannelSignatureRejectedError(
      `Expected a 0x-prefixed lowercase 64-hex-character digest; got ${JSON.stringify(value)}.`,
      { hint: "QIP-002 \xA74.4.3 rejects uppercase hex and non-32-byte lengths." }
    );
  }
  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i += 1) {
    bytes[i] = Number.parseInt(value.slice(2 + i * 2, 4 + i * 2), 16);
  }
  return bytes;
}
async function sha256(bytes) {
  const subtle = requireSubtle();
  const digest = await subtle.digest("SHA-256", toBufferSource(bytes));
  return new Uint8Array(digest);
}
function toBufferSource(value) {
  const buffer = new ArrayBuffer(value.byteLength);
  new Uint8Array(buffer).set(value);
  return new Uint8Array(buffer);
}
function normaliseIdentityBytes(identity, expectedLength, label) {
  let bytes;
  if (identity instanceof Uint8Array) {
    bytes = identity;
  } else if (typeof identity === "string") {
    const normalized = identity.startsWith("0x") ? identity.slice(2) : identity;
    if (normalized.length % 2 !== 0 || /[^0-9a-fA-F]/.test(normalized)) {
      throw new Qr20ChannelSignatureRejectedError(
        `${label} is not an even-length hex string.`
      );
    }
    bytes = new Uint8Array(normalized.length / 2);
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Number.parseInt(normalized.slice(i * 2, i * 2 + 2), 16);
    }
  } else {
    throw new Qr20ChannelSignatureRejectedError(
      `${label} must be a Uint8Array or hex string.`
    );
  }
  if (bytes.length !== expectedLength) {
    throw new Qr20ChannelSignatureRejectedError(
      `${label} must be exactly ${expectedLength} bytes; got ${bytes.length}.`
    );
  }
  return bytes;
}
async function computeChannelIdHash(channelId) {
  if (typeof channelId !== "string" || channelId.length === 0) {
    throw new TypeError("computeChannelIdHash: channelId must be a non-empty string.");
  }
  const digest = await sha256(utf8(channelId));
  return bytesToLowerHex(digest);
}
async function computeSigSchemesDigest(schemes) {
  if (!Array.isArray(schemes) || schemes.length === 0) {
    throw new TypeError("computeSigSchemesDigest: schemes must be a non-empty array.");
  }
  const sortedUnique = Array.from(new Set(schemes)).sort();
  const canonical = canonicalizeQr20Json(sortedUnique);
  const digest = await sha256(utf8(canonical));
  return bytesToLowerHex(digest);
}
async function computeIntentDigest(envelopes, priorDigest) {
  if (!Array.isArray(envelopes)) {
    throw new TypeError("computeIntentDigest: envelopes must be an array.");
  }
  let h = priorDigest === void 0 ? new Uint8Array(32) : digestHexToBytes(priorDigest);
  for (const envelope of envelopes) {
    const canonBytes = utf8(canonicalizeQr20Json(envelope));
    const combined = new Uint8Array(h.length + canonBytes.length);
    combined.set(h, 0);
    combined.set(canonBytes, h.length);
    h = await sha256(combined);
  }
  return bytesToLowerHex(h);
}
function canonicaliseStateUpdateForSigning(update) {
  return canonicalizeQr20Json({
    channel_id: update.channel_id,
    sequence: update.sequence,
    balances: {
      creator_amount: update.balances.creator_amount,
      resolver_amount: update.balances.resolver_amount
    },
    intent_digest: update.intent_digest
  });
}
async function verifySingleSignature(role, signature, canonBytes, identity, update, eip712Domain) {
  if (!identity) {
    throw new Qr20ChannelSignatureRejectedError(
      `${role} identity missing from validation context (QIP-002 \xA74.2.1 step 3).`,
      {
        hint: "Populate context.creator and context.resolver with the public keys / addresses captured at channel-open."
      }
    );
  }
  const signatureBytes = parseSignatureValue(signature.value, role);
  if (signature.scheme === "ed25519") {
    if (identity.publicKey === void 0) {
      throw new Qr20ChannelSignatureRejectedError(
        `${role} ed25519 identity missing publicKey (QIP-002 \xA74.2.1).`,
        { hint: "Set Qr20ChannelSignerIdentity.publicKey to the 32-byte raw ed25519 public key." }
      );
    }
    const publicKey = normaliseIdentityBytes(identity.publicKey, 32, `${role} ed25519 public key`);
    if (signatureBytes.length !== 64) {
      throw new Qr20ChannelSignatureRejectedError(
        `${role}_sig.value must be exactly 64 bytes for ed25519; got ${signatureBytes.length}.`
      );
    }
    const subtle = requireSubtle();
    const key = await subtle.importKey(
      "raw",
      toBufferSource(publicKey),
      { name: "Ed25519" },
      false,
      ["verify"]
    );
    const ok = await subtle.verify(
      { name: "Ed25519" },
      key,
      toBufferSource(signatureBytes),
      toBufferSource(canonBytes)
    );
    if (!ok) {
      throw new Qr20ChannelSignatureRejectedError(
        `${role}_sig: ed25519 signature is not valid for the canonicalised StateUpdate.`,
        {
          hint: "QIP-002 \xA74.2.1 requires pure Ed25519 over canon(StateUpdate) bytes.",
          remediation: "Re-sign the canonicalised StateUpdate bytes with the exact private key recorded at channel-open."
        }
      );
    }
    return;
  }
  if (signature.scheme === "secp256k1-eip191") {
    const digest = eip191Digest(canonBytes);
    const recovered = cryptoRecoverEthereumAddress(digest, signatureBytes);
    assertRecoveredAddressMatches(role, "secp256k1-eip191", recovered, identity, signatureBytes.length);
    return;
  }
  if (signature.scheme === "secp256k1-eip712") {
    if (!eip712Domain) {
      throw new Qr20ChannelSignatureRejectedError(
        `${role}_sig: secp256k1-eip712 requires context.eip712Domain (chainId + verifyingContract).`,
        {
          hint: "QIP-002 \xA74.2.4 binds the signed struct to a specific chain + channel contract.",
          remediation: "Populate context.eip712Domain with the chainId and verifyingContract of the on-chain channel contract recorded at channel-open."
        }
      );
    }
    const digest = await computeEip712StateUpdateDigest(update, eip712Domain);
    const recovered = cryptoRecoverEthereumAddress(digest, signatureBytes);
    assertRecoveredAddressMatches(role, "secp256k1-eip712", recovered, identity, signatureBytes.length);
    return;
  }
  if (signature.scheme === "secp256k1-sha256") {
    if (identity.publicKey === void 0) {
      throw new Qr20ChannelSignatureRejectedError(
        `${role} secp256k1-sha256 identity missing publicKey (QIP-002 \xA74.2.1).`,
        {
          hint: "Set Qr20ChannelSignerIdentity.publicKey to the 33-byte compressed or 65-byte uncompressed secp256k1 public key."
        }
      );
    }
    const publicKeyBytes = normaliseIdentityBytesFlexible(identity.publicKey, `${role} secp256k1-sha256 public key`);
    const digest = await sha256(canonBytes);
    const ok = verifyEcdsaSecp256k1(digest, signatureBytes, publicKeyBytes);
    if (!ok) {
      throw new Qr20ChannelSignatureRejectedError(
        `${role}_sig: secp256k1-sha256 signature is not valid for sha256(canon(StateUpdate)).`,
        {
          hint: "QIP-002 \xA74.2.1 uses DER-encoded ECDSA over sha256(canon(StateUpdate)) with chain-neutral public-key verification.",
          remediation: "Re-sign the sha256 digest of canonicaliseStateUpdateForSigning() with the secp256k1 private key recorded at channel-open and DER-encode the (r, s) pair."
        }
      );
    }
    return;
  }
  throw new Qr20ChannelSignatureRejectedError(
    `${role}_sig: unsupported signature scheme "${signature.scheme}".`,
    { hint: "QIP-002 \xA74.2.1 enumerates the supported schemes." }
  );
}
function parseSignatureValue(value, role) {
  if (typeof value !== "string" || !SIGNATURE_VALUE_PATTERN.test(value)) {
    throw new Qr20ChannelSignatureRejectedError(
      `${role}_sig.value must be a 0x-prefixed lowercase hex string.`,
      { hint: "QIP-002 \xA74.2.1 wire encoding." }
    );
  }
  const stripped = value.slice(2);
  if (stripped.length % 2 !== 0) {
    throw new Qr20ChannelSignatureRejectedError(
      `${role}_sig.value must have an even number of hex characters.`
    );
  }
  const bytes = new Uint8Array(stripped.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Number.parseInt(stripped.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}
function assertRecoveredAddressMatches(role, scheme, recovered, identity, signatureLength) {
  if (signatureLength !== 65) {
    throw new Qr20ChannelSignatureRejectedError(
      `${role}_sig.value must be exactly 65 bytes (r || s || v) for ${scheme}; got ${signatureLength}.`,
      { hint: "QIP-002 \xA74.2.1 requires r || s || v with v \u2208 {0, 1, 27, 28}." }
    );
  }
  if (recovered === void 0) {
    throw new Qr20ChannelSignatureRejectedError(
      `${role}_sig: ${scheme} signature could not be recovered for the canonicalised StateUpdate.`,
      { hint: "Check that the signature covers canon(StateUpdate) and v is 0/1/27/28." }
    );
  }
  if (identity.ethereumAddress === void 0) {
    throw new Qr20ChannelSignatureRejectedError(
      `${role} ${scheme} identity missing ethereumAddress (QIP-002 \xA74.2.1).`,
      { hint: "Set Qr20ChannelSignerIdentity.ethereumAddress to the lowercase 0x-prefixed 20-byte address captured at channel-open." }
    );
  }
  const expected = identity.ethereumAddress.toLowerCase();
  if (recovered !== expected) {
    throw new Qr20ChannelSignatureRejectedError(
      `${role}_sig: recovered address ${recovered} does not match the ${role} identity address ${expected}.`,
      { hint: "QIP-002 \xA74.2.1 step 3 requires the recovered signer to match the identity recorded at channel-open." }
    );
  }
}
function normaliseIdentityBytesFlexible(identity, label) {
  if (identity instanceof Uint8Array) {
    return identity;
  }
  if (typeof identity !== "string") {
    throw new Qr20ChannelSignatureRejectedError(`${label} must be a Uint8Array or hex string.`);
  }
  const normalized = identity.startsWith("0x") ? identity.slice(2) : identity;
  if (normalized.length % 2 !== 0 || /[^0-9a-fA-F]/.test(normalized)) {
    throw new Qr20ChannelSignatureRejectedError(`${label} is not an even-length hex string.`);
  }
  const bytes = new Uint8Array(normalized.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Number.parseInt(normalized.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}
async function computeEip712StateUpdateDigest(update, domain) {
  const name = domain.name ?? "QR20 Channel";
  const version = domain.version ?? "1";
  const chainId = normaliseChainId(domain.chainId);
  const channelIdHashHex = await computeChannelIdHash(update.channel_id);
  const domainSeparator = cryptoHashStruct(
    "EIP712Domain",
    [
      { name: "name", type: "string" },
      { name: "version", type: "string" },
      { name: "chainId", type: "uint256" },
      { name: "verifyingContract", type: "address" },
      { name: "salt", type: "bytes32" }
    ],
    {
      name,
      version,
      chainId,
      verifyingContract: domain.verifyingContract,
      salt: channelIdHashHex
    }
  );
  const messageHash = cryptoHashStruct(
    "StateUpdate",
    [
      { name: "channel_id", type: "bytes32" },
      { name: "sequence", type: "uint64" },
      { name: "creator_amount", type: "uint256" },
      { name: "resolver_amount", type: "uint256" },
      { name: "intent_digest", type: "bytes32" }
    ],
    {
      channel_id: channelIdHashHex,
      sequence: update.sequence,
      creator_amount: update.balances.creator_amount,
      resolver_amount: update.balances.resolver_amount,
      intent_digest: update.intent_digest
    }
  );
  return cryptoEip712Digest(domainSeparator, messageHash);
}
function normaliseChainId(chainId) {
  if (typeof chainId === "number") {
    if (!Number.isInteger(chainId) || chainId < 0) {
      throw new Qr20ChannelSignatureRejectedError(
        `eip712Domain.chainId must be a non-negative integer; got ${chainId}.`
      );
    }
    return chainId.toString();
  }
  if (typeof chainId === "string" && /^(0|[1-9][0-9]*)$/.test(chainId)) {
    return chainId;
  }
  if (typeof chainId === "string" && /^0x[0-9a-fA-F]+$/.test(chainId)) {
    return BigInt(chainId).toString();
  }
  throw new Qr20ChannelSignatureRejectedError(
    `eip712Domain.chainId must be a non-negative integer or decimal/hex string; got ${JSON.stringify(chainId)}.`
  );
}
async function verifyStateUpdateSignatures(update, context) {
  if (context.openSigSchemes !== void 0) {
    const schemeSet = new Set(context.openSigSchemes);
    for (const [role, sig] of [
      ["creator", update.creator_sig],
      ["resolver", update.resolver_sig]
    ]) {
      if (!schemeSet.has(sig.scheme)) {
        throw new Qr20ChannelSignatureRejectedError(
          `${role}_sig.scheme=${sig.scheme} is not in the channel's opened sig_schemes set.`,
          { hint: "QIP-002 \xA74.2.1 step 2 rejects out-of-set schemes." }
        );
      }
    }
  }
  const canonBytes = utf8(canonicaliseStateUpdateForSigning(update));
  await verifySingleSignature("creator", update.creator_sig, canonBytes, context.creator, update, context.eip712Domain);
  await verifySingleSignature("resolver", update.resolver_sig, canonBytes, context.resolver, update, context.eip712Domain);
}
export {
  QR20_CHANNEL_AMOUNT_PATTERN,
  QR20_CHANNEL_ASSET_REF_PATTERN,
  QR20_CHANNEL_CAIP10_PATTERN,
  QR20_CHANNEL_CAIP2_PATTERN,
  QR20_CHANNEL_DISPUTE_WINDOW_BOUNDS,
  QR20_CHANNEL_EVENT_NAMES,
  QR20_CHANNEL_INTENT_DIGEST_PATTERN,
  QR20_CHANNEL_ISO8601_UTC_PATTERN,
  QR20_CHANNEL_RAW_EVM_ADDRESS_PATTERN,
  QR20_CHANNEL_RECLAIM_WINDOW_FLOOR_SECONDS,
  QR20_CHANNEL_STATES,
  QR20_CHANNEL_TRANSITION_TRIGGERS,
  QR20_SIG_SCHEMES,
  Qr20ChannelNotImplementedError,
  Qr20ChannelSignatureRejectedError,
  canonicaliseStateUpdateForSigning,
  classifyStateUpdateReplay,
  compareDualSignedStates,
  computeChannelIdHash,
  computeIntentDigest,
  computeSigSchemesDigest,
  inferChannelPhase,
  validateExtendedChannel,
  validateNormalisedChannelEvent,
  validateReclaimWindow,
  validateStateTransition,
  verifyStateUpdateSignatures
};
